import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance'

// Async Thunks for API Calls

// Fetch paginated prescriptions
export const fetchPrescriptions = createAsyncThunk(
  "prescriptions/fetchAll",
  async ({ page = 1, pageSize = 5 }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/prescriptions/?page=${page}&page_size=${pageSize}`);
      
      // Ensure consultation and doctor data are available
      const updatedResults = response.data.results.map((prescription) => ({
        ...prescription,
        consultation: prescription.consultation || null,  // Ensure we don't crash if missing
        doctor: prescription.doctor || null,
      }));

      return { ...response.data, results: updatedResults };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch prescriptions");
    }
  }
);



// Fetch a single prescription detail
export const fetchPrescriptionDetail = createAsyncThunk(
  'prescriptions/fetchDetail',
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/prescriptions/${prescriptionId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch prescription details');
    }
  }
);

export const downloadPrescription = createAsyncThunk(
  "prescriptions/downloadPDF",
  async (prescriptionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/prescriptions/${prescriptionId}/download/`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `prescription_${prescriptionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return prescriptionId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to download prescription");
    }
  }
);


// Download prescription as PDF
// export const downloadPrescription = createAsyncThunk(
//   'prescriptions/downloadPDF',
//   async (prescriptionId, { rejectWithValue }) => {
//     try {
//       const response = await axiosInstance.get(`/prescriptions/${prescriptionId}/download/`, {
//         responseType: 'blob',
//       });

//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('download', `prescription_${prescriptionId}.pdf`);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();

//       return prescriptionId;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || 'Failed to download prescription');
//     }
//   }
// );

// Prescription Slice
const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState: {
    prescriptions: [],
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    loading: false,
    error: null,
  },
  reducers: {
    resetPrescriptions: (state) => {
      state.prescriptions = [];
      state.count = 0;
      state.next = null;
      state.previous = null;
      state.currentPage = 1;
    },
    resetPrescriptionDetail: (state) => {
      state.prescriptionDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload.results;
        state.count = action.payload.count;
        state.next = action.payload.next;
        state.previous = action.payload.previous;
        state.currentPage = action.meta.arg.page;
      })
      .addCase(fetchPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPrescriptionDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptionDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptionDetail = action.payload;
      })
      .addCase(fetchPrescriptionDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(downloadPrescription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadPrescription.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadPrescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetPrescriptions, resetPrescriptionDetail } = prescriptionsSlice.actions;
export default prescriptionsSlice.reducer;
